import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { formatDate, formatDateRelative } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Card, Divider, Icon, Label } from "simplydo/react/ui";

import { useAppSelector } from "store";
import util from "utils/utils";

import { AnonymousChip, ProfileChip } from "components/lib/Chips";
import { ShadowedCard } from "components/lib/UI";

const ChallengeMeta = styled.span`
  color: gray;
  display: block;
  margin: 2.5px 0;
  .i.icon {
    margin-right: 5px;
  }
`;

const MiniIdeaCard = styled.div`
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  background-color: #fff;
  margin: 0 8px;
  cursor: pointer;
  margin-top: 2px;

  &:hover {
    background-color: #e9ebee;
  }
  > img {
    height: 30px;
    width: 40px;
    border-radius: 4px;
    object-fit: cover;
    margin-right: 8px;
  }
  > div {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    .title {
      font-weight: bold;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      font-size: 0.9em;
      line-height: 1.3em;
    }
    > div {
      line-height: 0.9em;
      display: flex;
      align-items: center;
      > span {
        color: gray;
        font-size: 0.9em;
      }
    }
  }
`;

type ChallengeCardProps = {
  challenge: OpenAPI.GET<"/challenges/all">["response"]["challenges"][number];
  isNew?: boolean;
  ideas?: OpenAPI.Schemas["Idea"][] | undefined;
  showIdeas?: boolean;
  buttonText?: string;
};

const ChallengeCard = ({
  challenge,
  isNew,
  ideas,
  showIdeas,
  buttonText,
}: ChallengeCardProps) => {
  const user = useAppSelector((state) => state.user);
  const userIdeas = (ideas || []).filter((i) => i.user === user?._id);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <ShadowedCard>
      <Link to={util.challengeLink(challenge)}>
        <div
          style={{
            width: "100%",
            height: "75px",
            overflow: "hidden",
            backgroundImage: `url(${util.mixinCssUrlFallback(util.getImageUrl(challenge, ["bannerImage", "coverImage"], ["640x100", "300x300"]), util.challengeImage())})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center",
            borderTopLeftRadius: 7,
            borderTopRightRadius: 7,
          }}
        >
          {challenge.stage === "published" && (
            <Label
              size="small"
              color="green"
              style={{ marginTop: 7, marginLeft: -14 }}
              ribbon="right"
            >
              {t("generic.open")}
            </Label>
          )}
          {!challenge.stage && (
            <Label
              size="small"
              style={{ marginTop: 7, marginLeft: -14 }}
              color="yellow"
              ribbon="right"
            >
              {t("generic.draft")}
            </Label>
          )}
          {challenge.stage === "closed" && (
            <>
              <br />
              <Label
                size="small"
                style={{ marginTop: 7, marginLeft: -14 }}
                color="red"
                ribbon="right"
              >
                {t("generic.closed")}
              </Label>
            </>
          )}

          {challenge.stage === "published" && challenge.deadline && !isNew && (
            <>
              <br />
              <Label
                size="small"
                ribbon="right"
                style={{ marginTop: 7, marginLeft: -14 }}
                color="grey"
              >
                {t("generic.closes")}{" "}
                {formatDateRelative(challenge.deadline, { addSuffix: true })}
              </Label>
            </>
          )}

          {isNew && (
            <>
              <br />
              <Label
                size="small"
                color="blue"
                style={{ marginTop: 7, marginLeft: -14 }}
                ribbon="right"
              >
                New
              </Label>
            </>
          )}
        </div>
      </Link>

      <Card.Content style={{ position: "relative" }}>
        {challenge?.coverImageUrl && challenge.bannerImageUrl ?
          <>
            <div
              style={{
                width: 80,
                height: 80,
                overflow: "hidden",
                position: "absolute",
                marginTop: -80,
                boxShadow: "rgb(0 0 0 / 30%) 0.5px 2px 10px 4px",
                border: "2px solid white",
                backgroundImage: `url(${util.mixinCssUrlFallback(util.challengeImage(challenge), util.challengeImage())})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                borderRadius: 7,
              }}
            />
            <h3
              style={{
                marginBottom: challenge.tagline ? 5 : null,
                marginTop: 5,
              }}
            >
              <Link
                data-testid="challenge-card-name-link"
                to={util.challengeLink(challenge)}
                style={{ color: "#4d4949" }}
              >
                {challenge.name}
              </Link>
            </h3>
          </>
        : <h3
            style={{
              display: "block",
              flex: 1,
              marginBottom: challenge.tagline || userIdeas?.length ? 5 : null,
            }}
          >
            <Link
              to={util.challengeLink(challenge)}
              style={{ color: "#4d4949" }}
            >
              {challenge.name}
            </Link>
          </h3>
        }

        {challenge.tagline ?
          <ChallengeMeta>{challenge.tagline}</ChallengeMeta>
        : null}
        {userIdeas.length && !showIdeas ?
          <ChallengeMeta>
            You have{" "}
            {util.pluralise(
              userIdeas.length,
              `${t("generic.idea")}`,
              `${t("generic.ideas")}`,
            )}
          </ChallengeMeta>
        : null}
        <div>
          {challenge.postedByType === "anonymous" ?
            <AnonymousChip />
          : null}
          {(
            challenge.postedByType === "organisation" ||
            challenge.postedByType === "profile"
          ) ?
            <ProfileChip
              profile={challenge.postedBy}
              forType={challenge.postedByType}
            />
          : null}
        </div>
      </Card.Content>

      {showIdeas && userIdeas.length ?
        <div>
          <Divider
            horizontal
            fitted
            style={{ color: "gray", fontSize: "0.8em" }}
          >
            {t("challenge.yourIdeas.title")}
          </Divider>
          {userIdeas.map((idea) => (
            <MiniIdeaCard
              key={idea._id}
              color={idea.isSubmitted ? "green" : "beige"}
              onClick={() => navigate(`/ideas/${idea._id}`)}
            >
              <ImageWithFallback
                src={util.ideaCoverImage(idea, "640x100")}
                fallbackSrc={util.ideaCoverImage(undefined)}
                alt=""
              />
              <div>
                <span className="title">{idea.name}</span>
                <div>
                  <span>
                    {idea.isSubmitted ?
                      `Submitted ${formatDate(idea.submittedAt, "dd/MM")}`
                    : `Updated ${formatDate(idea.updatedAt, "dd/MM")}`}
                  </span>
                </div>
              </div>
            </MiniIdeaCard>
          ))}
          {challenge?.ideaCount && challenge.ideaCount > 1 ?
            <div
              style={{
                color: "gray",
                fontSize: "0.9em",
                textAlign: "right",
                paddingRight: 8,
              }}
            >
              +{challenge.ideaCount - userIdeas.length} {t("generic.more")}
            </div>
          : <div style={{ height: 8 }} />}
        </div>
      : null}

      <div
        className="content"
        style={{
          backgroundColor: theme.primaryColour,
          textAlign: "center",
          fontWeight: 600,
          maxHeight: 50,
        }}
      >
        <Link
          to={util.challengeLink(challenge)}
          style={{ color: "#fff", textTransform: "uppercase" }}
        >
          {buttonText ||
            t("generic.viewType", { entity: t("generic.challenge") })}{" "}
          <Icon name="arrow alternate circle right outline" />
        </Link>
      </div>
    </ShadowedCard>
  );
};

export default ChallengeCard;
